/**
 * Toast presentation — the fixed bottom-right container and a single toast card
 * (E10-F02-T03). State + timers live in {@link ToastProvider}; this file is the
 * chrome only.
 *
 * Parity with the legacy `core.js → showNotification` DOM:
 *   - container: `#toast-container`, fixed bottom-right, a vertical flex stack
 *     with a gap; `pointer-events-none` so it never blocks the UI underneath
 *     (each toast re-enables `pointer-events-auto` on itself);
 *   - card: `${bg} text-white px-6 py-4 rounded shadow-lg flex items-center
 *     gap-3 transform transition-all duration-300 … min-w-[300px]`, a leading
 *     Font Awesome icon + a `font-medium` message span;
 *   - appear: mounted with `translate-y-10 opacity-0`, the offset/opacity removed
 *     on the next animation frame so it slides up + fades in;
 *   - exit: the leave classes re-added (driven by `leaving` from the provider)
 *     for the reverse animation before unmount.
 *
 * The message is a React text child (`{toast.message}`) — never
 * `dangerouslySetInnerHTML` — so arbitrary text is intrinsically escaped (the
 * legacy `innerHTML` interpolation was the one XSS footgun we deliberately drop).
 */
import { useEffect, useState } from 'react';
import { TOAST_BG_CLASS, TOAST_ICON_CLASS, type Toast as ToastModel } from './toast-model.js';

export interface ToastItemProps {
  readonly toast: ToastModel;
  /**
   * Whether the toast is exiting (the provider flips this when the dismiss timer
   * fires); drives the reverse animation before the provider unmounts it.
   */
  readonly leaving: boolean;
  /** Manual dismiss (click the card). The provider runs its exit + removal. */
  readonly onDismiss: (id: string) => void;
}

/**
 * A single toast card. Owns ONLY its entrance animation latch (`entered`): it
 * mounts hidden (`translate-y-10 opacity-0`) and, on the next frame, flips
 * `entered` so React removes those classes and the CSS `transition-all
 * duration-300` plays the slide-up/fade-in. The exit state comes from the
 * provider (`leaving`) so the timing stays centralised with the removal.
 */
export function ToastItem({ toast, leaving, onDismiss }: ToastItemProps): React.JSX.Element {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // requestAnimationFrame parity: the legacy code appended the node with the
    // hidden classes, then removed them inside rAF so the transition fired.
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(() => setEntered(true))
        : (setTimeout(() => setEntered(true), 0) as unknown as number);
    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      else clearTimeout(raf);
    };
  }, []);

  // Hidden whenever it has not entered yet OR is leaving (reverse animation).
  const hidden = !entered || leaving;

  return (
    <div
      role="status"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      data-testid={`toast-${toast.type}`}
      data-toast-id={toast.id}
      onClick={() => onDismiss(toast.id)}
      className={[
        TOAST_BG_CLASS[toast.type],
        'text-white px-6 py-4 rounded shadow-lg flex items-center gap-3',
        'transform transition-all duration-300 pointer-events-auto min-w-[300px] cursor-pointer',
        hidden ? 'translate-y-10 opacity-0' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <i className={`fa-solid ${TOAST_ICON_CLASS[toast.type]} text-lg`} aria-hidden />
      <span className="font-medium">{toast.message}</span>
    </div>
  );
}

export interface ToastContainerProps {
  /** Toasts currently in the stack (oldest first → newest at the bottom). */
  readonly toasts: readonly ToastModel[];
  /** Ids currently playing their exit animation. */
  readonly leaving: ReadonlySet<string>;
  /** Manual dismiss handler. */
  readonly onDismiss: (id: string) => void;
}

/**
 * The fixed bottom-right toast stack. `pointer-events-none` on the container so
 * the empty area never intercepts clicks; individual cards opt back in.
 */
export function ToastContainer({
  toasts,
  leaving,
  onDismiss,
}: ToastContainerProps): React.JSX.Element {
  return (
    <div
      id="toast-container"
      data-testid="toast-container"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          leaving={leaving.has(toast.id)}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
